// src/components/NotificationConfig/NotificationConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faBell,
    faCog,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useNotificationContext } from '../../contexts/NotificationContext';
import EmailConfigForm from './forms/EmailConfigForm';
import TelegramConfigForm from './forms/TelegramConfigForm';
import PushConfigForm from './forms/PushConfigForm';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';

const NotificationConfig = () => {
    const { user } = useAuthStore();
    const {
        selectedMarca,
        setSelectedMarca,
        isChannelEnabled
    } = useNotificationContext();

    const [marcas, setMarcas] = useState([]);
    const [activeTab, setActiveTab] = useState('email');
    const [loading, setLoading] = useState(true);

    const loadMarcas = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get(`marcas/user/${user.userId}`);
            setMarcas(response);

            // Seleccionar la primera marca por defecto
            if (response.length > 0 && !selectedMarca) {
                setSelectedMarca(response[0]._id);
            }
        } catch (error) {
            console.error('Error cargando marcas:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.userId, selectedMarca, setSelectedMarca]);

    useEffect(() => {
        if (user?.userId) {
            loadMarcas();
        }
    }, [user, loadMarcas]);

    const tabs = [
        {
            id: 'email',
            name: 'Email',
            icon: faEnvelope,
            component: EmailConfigForm,
            description: 'Configurar notificaciones por correo electrónico'
        },
        {
            id: 'telegram',
            name: 'Telegram',
            icon: faTelegram,
            component: TelegramConfigForm,
            description: 'Configurar notificaciones por Telegram'
        },
        {
            id: 'push',
            name: 'Push',
            icon: faBell,
            component: PushConfigForm,
            description: 'Configurar notificaciones push del navegador'
        }
    ];

    const getStatusIcon = (channel) => {
        if (isChannelEnabled(channel)) {
            return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
        } else {
            return <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />;
        }
    };

    const getStatusText = (channel) => {
        return isChannelEnabled(channel) ? 'Configurado' : 'No configurado';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (marcas.length === 0) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-6">
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-500 text-xl mr-3" />
                    <div>
                        <h3 className="text-lg font-medium text-yellow-800">
                            No hay marcas configuradas
                        </h3>
                        <p className="text-yellow-700 mt-1">
                            Necesitas crear una marca antes de configurar las notificaciones.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                    <FontAwesomeIcon icon={faCog} className="text-blue-500 text-2xl mr-3" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                            Configuración de Notificaciones
                        </h2>
                        <p className="text-gray-600">
                            Configura los canales de notificación para tu marca
                        </p>
                    </div>
                </div>

                {/* Selector de marca */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar Marca
                    </label>
                    <select
                        value={selectedMarca || ''}
                        onChange={(e) => setSelectedMarca(e.target.value)}
                        className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {marcas.map((marca) => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Estado de canales */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {tabs.map((tab) => (
                        <div
                            key={tab.id}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${activeTab === tab.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                    <FontAwesomeIcon
                                        icon={tab.icon}
                                        className={`text-xl mr-2 ${activeTab === tab.id ? 'text-blue-500' : 'text-gray-500'
                                            }`}
                                    />
                                    <span className={`font-medium ${activeTab === tab.id ? 'text-blue-800' : 'text-gray-700'
                                        }`}>
                                        {tab.name}
                                    </span>
                                </div>
                                {getStatusIcon(tab.id)}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                {tab.description}
                            </p>
                            <div className="text-xs text-gray-500">
                                Estado: {getStatusText(tab.id)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Contenido del tab activo */}
            {selectedMarca && ActiveComponent && (
                <ActiveComponent marcaId={selectedMarca} />
            )}

            {/* Información adicional */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-6">
                <h3 className="text-lg font-medium text-blue-800 mb-3">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                    Información Importante
                </h3>
                <div className="space-y-2 text-sm text-blue-700">
                    <p>
                        • <strong>Email:</strong> Configura tu servidor SMTP para enviar notificaciones por correo electrónico.
                    </p>
                    <p>
                        • <strong>Telegram:</strong> Crea un bot en Telegram y configura el token y chat ID para recibir notificaciones.
                    </p>
                    <p>
                        • <strong>Push:</strong> Configura Firebase para enviar notificaciones push directamente al navegador de tus clientes.
                    </p>
                    <p>
                        • Puedes habilitar múltiples canales simultáneamente para mayor cobertura.
                    </p>
                    <p>
                        • Las notificaciones se enviarán automáticamente según los eventos configurados en tu estrategia de fidelización.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotificationConfig; 